import { apiPathToUrl } from '@lib/network/api';
import { signalWithTimeout } from '@lib/utils/upload-retry';
import { userStore } from '@stores/User';
import { useEffect, useState } from 'react';

/**
 * A deadline for learning where the redirect lands.
 *
 * The `finally` below cancels the response body, but it is not a deadline: it cannot run until
 * the `await` settles, and a connection that is accepted and then answers nothing never settles
 * it. Generous because this competes with nothing — a headers-only round trip that takes this
 * long is an endpoint that is not going to answer, and the alternative is a quality switch that
 * sits in its resolving state with no error to fall back on.
 */
const RESOLVE_TIMEOUT_MS = 30_000;

/**
 * Why an original could not be played, in the terms the viewer is told it in.
 *
 * `'unsupported-format'` is the browser refusing the file itself — the codec or container the
 * user uploaded. `'load-failed'` is everything between us and the bytes: the URL not resolving,
 * the network dropping, a presigned link that has expired. The two deserve different copy
 * because only the first is a verdict on the file.
 *
 * `'load-failed'` is a mixed bucket, and deliberately not split further: an expired URL or a
 * dropped connection clears on a retry, while a missing original or a bucket without CORS
 * headers never will, and neither the resolver's status code nor `MediaError` separates them
 * reliably. Its copy therefore offers a retry rather than directing one.
 */
export type OriginalUnavailableReason = 'unsupported-format' | 'load-failed';

/**
 * A URL for the media's original (untranscoded) file, usable as an `<a href>` or a
 * `<video src>`.
 *
 * `GET /library/{id}/media/{id}/file/original` answers with a 302 to a presigned URL, so the
 * fetch below exists only to learn where that redirect lands: the presigned URL carries its
 * own authorization, which keeps the user's bearer token out of an address that ends up in a
 * DOM attribute. The request is aborted the moment the headers are in — following the redirect
 * is what reveals the URL, downloading the body is not wanted here.
 *
 * The API documents a fallback where it streams the file instead of redirecting. There is no
 * URL to hand out in that case (the API address alone is unauthenticated), so this throws
 * rather than returning one that 401s at the point of use.
 *
 * @param filename - Sets the download filename via `Content-Disposition`. Omit for playback:
 *                   the header is harmless to a `<video>`, but there is nothing to name.
 * @throws If the request fails or outlives `RESOLVE_TIMEOUT_MS`, or the API streamed the file
 *         instead of redirecting.
 */
export async function resolveOriginalMediaUrl(
  libraryId: string,
  mediaId: string,
  filename?: string,
): Promise<string> {
  const path = `/library/${libraryId}/media/${mediaId}/file/original`;
  const requestUrl = apiPathToUrl(
    filename ? `${path}?filename=${encodeURIComponent(filename)}` : path,
  );

  const abortController = new AbortController();
  let response: Response;
  try {
    response = await fetch(requestUrl, {
      headers: {
        Authorization: `Bearer ${userStore.token}`,
        // Only the redirect's destination is wanted, so ask for one byte. Without it the origin
        // starts streaming a file that can be gigabytes, and the abort below arrives after it
        // has already put a burst on the wire.
        Range: 'bytes=0-0',
      },
      // Both bounds apply: the timeout ends a silent endpoint, the local controller ends the
      // body once the headers are in.
      signal: signalWithTimeout(abortController.signal, RESOLVE_TIMEOUT_MS),
    });
  } finally {
    abortController.abort();
  }

  if (!response.ok) {
    throw new Error(`Original file unavailable (HTTP ${response.status})`);
  }

  const resolvedUrl = response.url;
  if (!resolvedUrl || resolvedUrl === requestUrl) {
    throw new Error('Original file was streamed rather than redirected to a presigned URL');
  }

  return resolvedUrl;
}

/**
 * Resolutions in flight, keyed by library and media.
 *
 * Two `VideoComponent`s can hold the same clip during a transition and would otherwise each
 * probe for the same URL. Only the in-flight promise is shared, never a settled one: the URL it
 * resolves to is presigned and expires, so a later switch must ask again.
 */
const inFlightResolutions = new Map<string, Promise<string>>();

function resolveOriginalMediaUrlOnce(libraryId: string, mediaId: string): Promise<string> {
  const key = `${libraryId}/${mediaId}`;
  const existing = inFlightResolutions.get(key);
  if (existing) return existing;

  const resolution = resolveOriginalMediaUrl(libraryId, mediaId).finally(() => {
    inFlightResolutions.delete(key);
  });
  inFlightResolutions.set(key, resolution);
  return resolution;
}

/**
 * Resolves the original file's URL while `enabled`, for players that let the viewer watch the
 * source file instead of the transcoded preview.
 *
 * The URL is presigned and therefore expires; it is resolved once per enable, not refreshed.
 * A player whose playback dies on an expired URL is expected to fall back to the preview
 * rather than silently re-resolving — see `VideoComponent`'s `onOriginalUnavailable`.
 */
export function useOriginalMediaUrl(
  libraryId: string | undefined,
  mediaId: string | undefined,
  enabled: boolean,
): { url: string | null; error: Error | null } {
  const [state, setState] = useState<{ url: string | null; error: Error | null }>({
    url: null,
    error: null,
  });

  useEffect(() => {
    if (!enabled || !libraryId || !mediaId) {
      // Identity-preserving so a disabled hook doesn't re-render its component on every
      // dependency change — this runs on every mount of every player that never asks for
      // the original, which is nearly all of them.
      setState((current) => (current.url || current.error ? { url: null, error: null } : current));
      return;
    }

    let current = true;
    resolveOriginalMediaUrlOnce(libraryId, mediaId)
      .then((url) => {
        if (current) setState({ url, error: null });
      })
      .catch((error: Error) => {
        console.error('Failed to resolve the original media URL:', error);
        if (current) setState({ url: null, error });
      });

    return () => {
      current = false;
    };
  }, [libraryId, mediaId, enabled]);

  return state;
}
